import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Application, SerializableIcon, setApplications, setError, setLoading } from "../app/stores/applications-slice";
import { RootState } from "../app/stores/store";
import { GridPosition, updateApplicationGridPosition } from "../app/stores/user-settings.slice";
import { useIndexedDBContext } from "../providers/indexed-db-provider";
import { useUserSettings } from "./use-user-settings";

// Define icon types
type IconType = "Folder" | "Settings" | "Chrome" | "FileText" | "Youtube" | "AlertCircle";

// Define application type with icon as string
interface ApplicationData {
    id: string;
    name: string;
    iconType: IconType;
    iconColor: string;
    path: string;
    category: string;
    isPinned: boolean;
    defaultGridPosition?: GridPosition;
}

const DEFAULT_APPLICATIONS: ApplicationData[] = [
    {
        id: "1",
        name: "File Explorer",
        iconType: "Folder",
        iconColor: "text-yellow-500",
        path: "/file-explorer",
        category: "System",
        isPinned: true,
        defaultGridPosition: { x: 0, y: 0 },
    },
    {
        id: "2",
        name: "Settings",
        iconType: "Settings",
        iconColor: "text-gray-500",
        path: "/settings",
        category: "System",
        isPinned: true,
        defaultGridPosition: { x: 1, y: 0 },
    },
    {
        id: "3",
        name: "Chrome",
        iconType: "Chrome",
        iconColor: "text-blue-500",
        path: "/chrome",
        category: "google",
        isPinned: true,
        defaultGridPosition: { x: 2, y: 0 },
    },
    {
        id: "4",
        name: "Readme.txt",
        iconType: "FileText",
        iconColor: "text-blue-400",
        path: "/readme",
        category: "System",
        isPinned: true,
        defaultGridPosition: { x: 3, y: 0 },
    },
    {
        id: "5",
        name: "Youtube Viewer",
        iconType: "Youtube",
        iconColor: "text-red-500",
        path: "/youtube-viewer",
        category: "google",
        isPinned: true,
        defaultGridPosition: { x: 4, y: 0 },
    },
    {
        id: "6",
        name: "Log Monitor",
        iconType: "AlertCircle",
        iconColor: "text-red-400",
        path: "/log-monitor",
        category: "System",
        isPinned: true,
        defaultGridPosition: { x: 5, y: 0 },
    },
];

// Helper function to convert ApplicationData to Application
const convertToApplication = (appData: ApplicationData, userGridPosition?: GridPosition): Application => {
    const iconData: SerializableIcon = {
        type: appData.iconType,
        color: appData.iconColor,
    };

    return {
        id: appData.id,
        name: appData.name,
        iconData,
        path: appData.path,
        category: appData.category,
        isPinned: appData.isPinned,
        gridPosition: userGridPosition || appData.defaultGridPosition,
    };
};

// Helper function to convert Application to ApplicationData
const convertToApplicationData = (app: Application): ApplicationData => {
    return {
        id: app.id,
        name: app.name,
        iconType: app.iconData.type as IconType,
        iconColor: app.iconData.color,
        path: app.path,
        category: app.category,
        isPinned: app.isPinned,
        defaultGridPosition: app.gridPosition,
    };
};

export function useApplications() {
    const dispatch = useDispatch();
    const { applicationsDb } = useIndexedDBContext();
    const { data: applicationsData, isLoading, error, addItem, updateItem, deleteItem, getAllItems } = applicationsDb;
    const isInitialized = useRef(false);
    const userSettings = useSelector((state: RootState) => state.userSettings.settings);
    const { updateSettings } = useUserSettings();

    // Convert ApplicationData to Application for Redux store
    const applications = (applicationsData as unknown as ApplicationData[]).map((appData) =>
        convertToApplication(appData, userSettings.applicationGridPositions[appData.id])
    );

    useEffect(() => {
        const initializeApplications = async () => {
            if (isInitialized.current) return;

            dispatch(setLoading(true));

            try {
                const currentAppsData = (await getAllItems()) as unknown as ApplicationData[];

                if (currentAppsData.length === 0) {
                    // If no applications exist, add default ones
                    for (const appData of DEFAULT_APPLICATIONS) {
                        await addItem(appData as unknown as Application);
                    }
                    // After adding all default apps, get the updated list

                    const updatedAppsData = (await getAllItems()) as unknown as ApplicationData[];

                    // Convert ApplicationData to Application
                    const updatedApps = updatedAppsData.map((appData) =>
                        convertToApplication(appData, userSettings.applicationGridPositions[appData.id])
                    );
                    dispatch(setApplications(updatedApps));
                } else {
                    // Convert ApplicationData to Application
                    const convertedApps = currentAppsData.map((appData) =>
                        convertToApplication(appData, userSettings.applicationGridPositions[appData.id])
                    );
                    dispatch(setApplications(convertedApps));
                }

                isInitialized.current = true;
            } catch (err) {
                dispatch(setError(err instanceof Error ? err.message : "Failed to load applications"));
            } finally {
                dispatch(setLoading(false));
            }
        };

        initializeApplications();
    }, [dispatch, getAllItems, addItem, userSettings.applicationGridPositions]);

    const handleApplicationGridPosition = useCallback(
        async (applicationId: string, x: number, y: number) => {
            const position: GridPosition = { x, y };

            // Update Redux store
            dispatch(updateApplicationGridPosition({ applicationId, position }));

            // Update IndexedDB through userSettings
            await updateSettings({
                ...userSettings,
                applicationGridPositions: {
                    ...userSettings.applicationGridPositions,
                    [applicationId]: position,
                },
            });

            // Refresh the applications list
            const updatedAppsData = (await getAllItems()) as unknown as ApplicationData[];
            const updatedApps = updatedAppsData.map((appData) =>
                convertToApplication(appData, userSettings.applicationGridPositions[appData.id])
            );
            dispatch(setApplications(updatedApps));
        },
        [dispatch, updateSettings, userSettings, getAllItems]
    );

    return {
        applications,
        isLoading,
        error,
        addApplication: async (app: Omit<Application, "id">) => {
            const newApp = convertToApplication({
                ...convertToApplicationData(app as Application),
                id: crypto.randomUUID(),
            });
            await addItem(newApp);
            const updatedAppsData = (await getAllItems()) as unknown as ApplicationData[];
            const updatedApps = updatedAppsData.map((appData) =>
                convertToApplication(appData, userSettings.applicationGridPositions[appData.id])
            );
            dispatch(setApplications(updatedApps));
            return newApp;
        },
        updateApplication: async (app: Application) => {
            await updateItem(app);
            const updatedAppsData = (await getAllItems()) as unknown as ApplicationData[];
            const updatedApps = updatedAppsData.map((appData) =>
                convertToApplication(appData, userSettings.applicationGridPositions[appData.id])
            );
            dispatch(setApplications(updatedApps));
        },
        removeApplication: async (id: string) => {
            await deleteItem(id);
            const updatedAppsData = (await getAllItems()) as unknown as ApplicationData[];
            const updatedApps = updatedAppsData.map((appData) =>
                convertToApplication(appData, userSettings.applicationGridPositions[appData.id])
            );
            dispatch(setApplications(updatedApps));
        },
        updateApplicationGridPosition: handleApplicationGridPosition,
    };
}
